/**
 * 瓦片协议处理器 —— 底图的**唯一**取数路径（缓存优先）。
 *
 * ## 为什么要接管取图
 *
 * 样式里的底图源指向 `hgc://tiles/<源>/…`，于是每一块瓦片都要经过这里。
 * 这一步换来三件浏览器做不到的事：
 *
 * 1. **可控的离线缓存**：自己写在 IndexedDB 里，容量、过期、淘汰都是明确的，
 *    而不是"浏览器自己决定 HTTP 缓存里还剩什么"；
 * 2. **离线可判定**：请求经过这里，就知道用户**真的需要**哪一块瓦片，
 *    于是"当前视野缺哪些"才有答案（见 `coverage.ts`）；
 * 3. **断网不空白**：断网且没有缓存时返回一张透明占位瓦片，同时把缺口记下来 ——
 *    界面于是可以画"这里没有缓存"而不是给用户一片死寂的纯色。
 *
 * ## 过期不等于不可用
 *
 * 在线时过期瓦片走 stale-while-revalidate：先把旧图交出去（不阻塞渲染），
 * 后台再去刷新。离线时**完全忽略 TTL** —— 野外唯一能用的地图不该因为
 * "30 天前的数据"而不显示；过期只影响"要不要去联网"，不影响"能不能画"。
 *
 * ## 失败一律不抛
 *
 * 除了"这个 URL 根本不是我们生成的"（那是编程错误，应当大声报错）之外，
 * 任何失败都返回占位瓦片并发出事件。让 `addProtocol` 抛出去的结果是
 * 一片没有提示的空白 —— 那正是本项目最想避免的那种失败。
 */

import type { BaseMapSource } from './sources';
import { resolveTileUrl } from './sources';
import {
  entryFor,
  isExpired,
  type TileEntry,
  type TileStore,
} from './tileStore';
import { tileKey, parseHgcTileUrl, type TileCoord } from './tiles';

/** 资源策略：`online` 允许联网，`cache-only` 只用缓存（用户显式选择，或浏览器报断网）。 */
export type MapResourceMode = 'online' | 'cache-only';

/**
 * 1×1 全透明 PNG。断网且没缓存时用它顶上。
 *
 * 为什么是透明而不是"灰色方块"：透明会让底图下面我们自己的背景色（同样来自令牌）
 * 露出来，深浅主题都能看；而画一个灰色的假瓦片会容易被误当成"这块地形就是这样"。
 * 真正提示"这里没缓存"的是覆盖层（`missingTilesLayerSpecs`）和文字清单。
 */
export const PLACEHOLDER_TILE_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII=';

let placeholderBytes: ArrayBuffer | null = null;

export function placeholderTileBytes(): ArrayBuffer {
  if (placeholderBytes !== null) return placeholderBytes;
  const binary = atob(PLACEHOLDER_TILE_BASE64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  placeholderBytes = bytes.buffer;
  return placeholderBytes;
}

export interface TileHttpResponse {
  readonly status: number;
  readonly ok: boolean;
  readonly contentType: string;
  readonly data: ArrayBuffer;
}

/** 上游明确表示"这里没有瓦片"的状态码。 */
const NO_TILE_STATUSES = new Set([204, 404, 410]);

/** 密钥被拒绝的状态码 —— 与"没数据"必须分开：前者要用户去改密钥，后者不用做什么。 */
const AUTH_STATUSES = new Set([401, 403]);

/**
 * 真实下载一块瓦片。
 *
 * `credentials: 'omit'`：瓦片来自第三方主机，没有理由把本应用的 Cookie 发过去；
 * 需要凭据的服务（例如内网）请用带 `{key}` 的模板，而不是靠 Cookie。
 */
export async function fetchTileBytes(
  url: string,
  signal: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<TileHttpResponse> {
  const response = await fetchImpl(url, { signal, credentials: 'omit', cache: 'default' });
  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok) {
    // 失败响应不读 body：这些响应往往带着整页 HTML 错误页，读它是纯浪费带宽
    return { status: response.status, ok: false, contentType, data: new ArrayBuffer(0) };
  }
  const data = await response.arrayBuffer();
  return { status: response.status, ok: true, contentType, data };
}

export type TileEvent =
  /** 命中缓存。`stale` 表示它已过期但在线时仍被先交出去了 */
  | { readonly type: 'hit'; readonly sourceId: string; readonly tile: TileCoord; readonly stale: boolean }
  /** 缓存里没有，需要联网 */
  | { readonly type: 'miss'; readonly sourceId: string; readonly tile: TileCoord }
  /** 刚写进缓存 */
  | { readonly type: 'stored'; readonly sourceId: string; readonly tile: TileCoord; readonly bytes: number }
  /** 上游明确说没有这块瓦片 */
  | { readonly type: 'empty'; readonly sourceId: string; readonly tile: TileCoord }
  /** 离线且没有缓存 —— 用户看到的就是"这里没图" */
  | { readonly type: 'offline-miss'; readonly sourceId: string; readonly tile: TileCoord }
  /** 需要密钥但没填 */
  | { readonly type: 'no-key'; readonly sourceId: string }
  /** 密钥被拒（401/403） */
  | { readonly type: 'unauthorized'; readonly sourceId: string }
  /** 未知源（样式里写了一个没注册的 id） */
  | { readonly type: 'no-source'; readonly sourceId: string }
  | { readonly type: 'error'; readonly sourceId: string; readonly tile: TileCoord; readonly message: string };

export interface TileProtocolDeps {
  readonly store: TileStore;
  readonly source: (sourceId: string) => BaseMapSource | undefined;
  readonly key: (sourceId: string) => string | null;
  readonly mode: () => MapResourceMode;
  readonly now?: () => number;
  readonly fetchTile?: (url: string, signal: AbortSignal) => Promise<TileHttpResponse>;
  readonly onEvent?: (event: TileEvent) => void;
  readonly pixelRatio?: number;
}

export interface TileProtocolRequest {
  readonly url: string;
  readonly signal: AbortSignal;
}

/**
 * 处理一次瓦片请求。返回**编码后的瓦片字节**（MapLibre 会自己解码图片）。
 *
 * 返回值刻意是"字节"而不是已解码的 `ImageBitmap`：解码要 `createImageBitmap`，
 * 而它在部分浏览器/离线 Worker 里不可用，多一层失败可能。字节路径是纯内存操作。
 */
export async function handleTileRequest(
  deps: TileProtocolDeps,
  request: TileProtocolRequest,
): Promise<ArrayBuffer> {
  const now = deps.now ?? ((): number => Date.now());
  const emit = deps.onEvent ?? ((): void => {});

  const parsed = parseHgcTileUrl(request.url);
  if (parsed === null) {
    // 这个 URL 是我们自己生成的，解析失败说明模板拼错了 —— 大声报出来
    throw new Error(`无法解析地图瓦片 URL：${request.url}`);
  }
  const { sourceId, tile } = parsed;
  const source = deps.source(sourceId);
  if (source === undefined) {
    emit({ type: 'no-source', sourceId });
    return placeholderTileBytes();
  }

  const key = tileKey(sourceId, tile);
  const cached = await deps.store.get(key);
  const online = deps.mode() === 'online';

  if (cached !== null) {
    if (cached.state === 'ok') {
      const stale = isExpired(cached, now());
      if (online && stale) {
        // 先把旧图交出去，刷新在后台跑（不阻塞这一帧）
        void download(deps, source, tile, { background: true });
      }
      emit({ type: 'hit', sourceId, tile, stale });
      return cached.data;
    }
    // 已知"上游没有这块瓦片"：TTL 内直接给占位图，不再打扰服务器
    if (!isExpired(cached, now())) {
      emit({ type: 'empty', sourceId, tile });
      return placeholderTileBytes();
    }
  }

  if (!online) {
    emit({ type: 'offline-miss', sourceId, tile });
    return placeholderTileBytes();
  }

  emit({ type: 'miss', sourceId, tile });
  return download(deps, source, tile, { background: false });
}

interface DownloadOptions {
  /** 后台刷新：失败只记事件，不影响已经交出去的旧图 */
  readonly background: boolean;
}

async function download(
  deps: TileProtocolDeps,
  source: BaseMapSource,
  tile: TileCoord,
  options: DownloadOptions,
): Promise<ArrayBuffer> {
  const now = deps.now ?? ((): number => Date.now());
  const emit = deps.onEvent ?? ((): void => {});
  const fetchTile = deps.fetchTile ?? ((url: string, signal: AbortSignal) => fetchTileBytes(url, signal));

  const url = resolveTileUrl(source, tile, deps.key(source.id), deps.pixelRatio ?? 1);
  if (url === null) {
    // 需要密钥却没填（或模板里还有我们替换不掉的占位符）
    emit({ type: 'no-key', sourceId: source.id });
    return placeholderTileBytes();
  }

  const controller = new AbortController();
  try {
    const response = await fetchTile(url, controller.signal);
    if (AUTH_STATUSES.has(response.status)) {
      emit({ type: 'unauthorized', sourceId: source.id });
      return placeholderTileBytes();
    }
    if (!response.ok || NO_TILE_STATUSES.has(response.status)) {
      if (response.ok) {
        await deps.store.put(
          entryFor({
            sourceId: source.id,
            tile,
            data: new ArrayBuffer(0),
            contentType: '',
            state: 'empty',
            now: now(),
          }),
        );
        emit({ type: 'empty', sourceId: source.id, tile });
      } else {
        // 服务端错误**不**缓存：5xx 是暂时的，把它记成"这里没数据"会让用户
        // 在服务恢复后仍然看到一片空白，而且他自己没法知道为什么
        emit({
          type: 'error',
          sourceId: source.id,
          tile,
          message: `底图服务器返回 ${response.status}`,
        });
      }
      return placeholderTileBytes();
    }

    const entry = entryFor({
      sourceId: source.id,
      tile,
      data: response.data,
      contentType: response.contentType,
      state: 'ok',
      now: now(),
    });
    await deps.store.put(entry);
    emit({ type: 'stored', sourceId: source.id, tile, bytes: response.data.byteLength });
    return response.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (!options.background || !isAbort(error)) {
      emit({ type: 'error', sourceId: source.id, tile, message });
    }
    return placeholderTileBytes();
  }
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * 把一批瓦片的缓存状态汇总成一句中文（供界面显示）。
 *
 * 四类必须分开报：**命中缓存**（这一块本来就在本机）、**联网取图**（缓存没有、
 * 但联网取回来了 → 会新增占用）、**离线缺图**（断网且没有缓存 → 屏幕上是一块空白）、
 * **取图失败**（服务端拒绝/密钥不对/网络异常 → 要用户去处理）。
 * 只报其中两类的话，用户会看到"命中缓存 21、缺缓存 0"却不知道另外 40 次请求干了什么 ——
 * 而那句话正是他判断"这片区域到底缓存上没有"的依据。
 */
export function describeEvents(events: readonly TileEvent[]): string {
  let hit = 0;
  let fetched = 0;
  let offline = 0;
  let failed = 0;
  for (const event of events) {
    if (event.type === 'hit') hit += 1;
    else if (event.type === 'miss' || event.type === 'stored') fetched += 1;
    else if (event.type === 'offline-miss') offline += 1;
    else if (event.type === 'error' || event.type === 'unauthorized' || event.type === 'no-key') failed += 1;
  }
  if (events.length === 0) return '本次没有发起瓦片请求';
  const parts = [`命中缓存 ${hit}`, `联网取图 ${fetched}`, `离线缺图 ${offline}`, `取图失败 ${failed}`];
  return `本次共 ${events.length} 次瓦片请求：${parts.join('、')}`;
}

/** 缓存里这条记录能不能直接用（离线判定：不看 TTL）。 */
export function usableOffline(entry: TileEntry | null): boolean {
  return entry !== null && entry.state === 'ok' && entry.data.byteLength > 0;
}
